import React, { useState, useRef } from "react";
// 1. Import styles as a module
import styles from "./LandingPage.module.css";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import ReactPlayer from "react-player";
import { 
  ChevronDown, Menu, X, Play, Sparkles, Zap, ShieldCheck, 
  Type, Subtitles, Image as ImageIcon, Music, Video, Layers 
} from "lucide-react";

import { analytics } from "../../firebase/firebase";
import { logEvent } from "firebase/analytics";
import { trackEvent } from "../../utils/analyticsService";

import Video1 from "../../assets/Video1.mp4";
import Video2 from "../../assets/Video2.mp4";
import Video3 from "../../assets/Video3.mp4";

import BuyPremium from "../Subscription/Buypremium";

const faqs = [
  {
    question: "Is the AI Video Generator free?",
    answer: "Yes. You can start generating videos for free and upgrade later whenever you need more exports and premium features."
  },
  {
    question: "How long does it take?",
    answer: "Most videos are generated within seconds depending on length and selected quality."
  },
  {
    question: "Can I create YouTube Shorts?",
    answer: "Yes. Generate videos optimized for YouTube Shorts, TikTok and Instagram Reels."
  },
  {
    question: "Do videos include subtitles?",
    answer: "Yes. AI automatically generates subtitles making your videos more engaging."
  }
];

const features = [
  {
    icon: <Type size={24} />,
    title: "AI Script Generation",
    text: "Turn any prompt or idea into a highly engaging, ready-to-publish video script instantly.",
    span: "colSpan2" // Updated for module
  },
  {
    icon: <Zap size={24} />,
    title: "AI Voiceovers",
    text: "Natural, human-sounding narration in multiple languages and tones.",
    span: "colSpan1"
  },
  {
    icon: <Subtitles size={24} />,
    title: "Auto Captions",
    text: "Dynamic, animated captions generated automatically to boost viewer retention.",
    span: "colSpan1"
  },
  {
    icon: <ImageIcon size={24} />,
    title: "Premium Stock",
    text: "Access millions of high-quality visuals perfectly matched to your story.",
    span: "colSpan2"
  },
  {
    icon: <ShieldCheck size={24} />,
    title: "HD Export",
    text: "Render and download crisp 1080p and 4K videos.",
    span: "colSpan1"
  },
  {
    icon: <Video size={24} />,
    title: "No Watermark",
    text: "Keep your brand front and center on premium plans.",
    span: "colSpan1"
  },
  {
    icon: <Music size={24} />,
    title: "Background Music",
    text: "Royalty-free tracks synced to the mood of your video.",
    span: "colSpan1"
  },
  {
    icon: <Layers size={24} />,
    title: "Auto Scenes",
    text: "Smart scene transitions based on script context and pacing.",
    span: "colSpan2"
  }
];

const LandingPage = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeFAQ, setActiveFAQ] = useState(null);

  const pricingRef = useRef(null);
  const featureRef = useRef(null);
  const faqRef = useRef(null);

  const scrollTo = (ref) => {
    setMenuOpen(false);
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  const toggleFAQ = (index) => {
    setActiveFAQ(index === activeFAQ ? null : index);
    logEvent(analytics, "faq_clicked", { index });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Generator.video",
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": "Generate AI videos, YouTube Shorts, TikTok videos and Instagram Reels instantly using our Free Video Generator."
  };

  return (
    <HelmetProvider>
      <Helmet>
        <title>Free Video Generator | AI Text to Video Generator</title>
        <meta name="description" content="Generate AI videos, YouTube Shorts, TikTok videos and Instagram Reels instantly using our Free Video Generator." />
        <meta name="keywords" content="Free Video Generator, AI Video Generator, Text To Video, Shorts Generator, TikTok Video Generator" />
        <meta property="og:title" content="Free Video Generator" />
        <meta property="og:description" content="Create professional AI videos from text in seconds." />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href="https://generator.video/" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <div className={styles.premiumLanding}>
        {/* ================= NAVBAR ================= */}
        <nav className={styles.premiumNavbar}>
          <div className={styles.navContainer}>
            <div className={styles.navLogo}>
              <div className={styles.logoMark}>
                <Sparkles size={16} />
              </div>
              <span>Generator.video</span>
            </div>

            <div className={`${styles.navLinks} ${menuOpen ? styles.open : ""}`}>
              <button onClick={() => scrollTo(featureRef)}>Features</button>
              <button onClick={() => scrollTo(pricingRef)}>Pricing</button>
              <button onClick={() => scrollTo(faqRef)}>FAQ</button>
              <Link 
                to="/Dashboard" 
                className={`${styles.navCta} ${styles.mobileCta}`}
                onClick={() => trackEvent("landing_generate_clicked")}
              >
                Generate Free
              </Link>
            </div>

            <div className={styles.navActions}>
              <Link 
                to="/Dashboard" 
                className={`${styles.navCta} ${styles.desktopCta}`}
                onClick={() => trackEvent("landing_generate_clicked")}
              >
                Generate Free
              </Link>
              <button className={styles.menuToggle} onClick={() => setMenuOpen(!menuOpen)}>
                {menuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>
        </nav>

        <main>
          {/* ================= HERO ================= */}
          <section className={styles.heroSection}>
            <div className={styles.heroBackgroundGlow}></div>
            <div className={styles.heroContent}>
              <div className={styles.heroBadge}>
                <Sparkles size={14} />
                <span>AI Powered Video Creation</span>
              </div>
              
              <h1 className={styles.heroTitle}>
                Free Video <br />
                <span className={styles.textGradient}>Generator</span>
              </h1>
              
              <p className={styles.heroSubtitle}>
                Create professional AI videos from text in seconds.<br/>
                Generate YouTube Shorts, TikTok, Instagram Reels, faceless videos and marketing videos using AI.
              </p>
              
              <div className={styles.heroActions}>
                <Link 
                  to="/Dashboard" 
                  className={`${styles.btnPrimary} ${styles.large}`}
                  onClick={() => trackEvent("landing_hero_cta")}
                >
                  Generate Free
                </Link>
              </div>
            </div>

            <div className={styles.heroVisual}>
              <div className={styles.aiPreviewCard}>
                <div className={styles.windowHeader}>
                  <div className={styles.dots}><span></span><span></span><span></span></div>
                  <div className={styles.windowTitle}>Generator.ai</div>
                </div>
                <div className={styles.windowBody}>
                  <div className={styles.promptBox}>
                    <span className={styles.cursor}>Create a motivational business short...</span>
                  </div>
                  <div className={styles.workflowSteps}>
                    <div className={`${styles.stepItem} ${styles.step1}`}><CheckCircle /> <span>Writing Script</span></div>
                    <div className={`${styles.stepItem} ${styles.step2}`}><CheckCircle /> <span>Finding Stock Videos</span></div>
                    <div className={`${styles.stepItem} ${styles.step3}`}><CheckCircle /> <span>AI Voice Generated</span></div>
                    <div className={`${styles.stepItem} ${styles.step4}`}><CheckCircle /> <span>Captions Added</span></div>
                  </div>
                  <div className={styles.progressContainer}>
                    <div className={styles.progressBar}></div>
                  </div>
                  <div className={styles.successMessage}>
                    ✨ Video Ready
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* ================= SOCIAL PROOF ================= */}
          <section className={styles.socialProof}>
            <div className={styles.statsGrid}>
              <div className={styles.statCard}>
                <h3>10K+</h3>
                <p>Videos Generated</p>
              </div>
              <div className={styles.statCard}>
                <h3>5K+</h3>
                <p>Creators</p>
              </div>
              <div className={styles.statCard}>
                <h3>50+</h3>
                <p>Countries</p>
              </div>
              <div className={styles.statCard}>
                <h3>99.9%</h3>
                <p>Uptime</p>
              </div>
            </div>
          </section>

          {/* ================= HOW IT WORKS ================= */}
          <section className={styles.howItWorks}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>From idea to video in seconds.</h2>
            </div>
            <div className={styles.stepsContainer}>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>1</div>
                <h3>Write Prompt</h3>
                <p>Enter your topic, script, or let our AI brainstorm ideas for you.</p>
              </div>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>2</div>
                <h3>Choose Style</h3>
                <p>Select templates for Shorts, Reels, TikTok, or widescreen marketing formats.</p>
              </div>
              <div className={styles.stepCard}>
                <div className={styles.stepNumber}>3</div>
                <h3>Generate Video</h3>
                <p>AI automatically compiles visuals, captions, and voiceovers instantly.</p>
              </div>
            </div>
          </section>

          {/* ================= FEATURES (BENTO) ================= */}
          <section className={styles.featuresSection} ref={featureRef}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Everything you need to scale.</h2>
            </div>
            <div className={styles.bentoGrid}>
              {features.map((feature, idx) => (
                <div key={idx} className={`${styles.bentoCard} ${styles[feature.span]}`}>
                  <div className={styles.bentoIcon}>{feature.icon}</div>
                  <h4>{feature.title}</h4>
                  <p>{feature.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* ================= VIDEO SHOWCASE ================= */}
          <section className={styles.showcaseSection}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Built for modern platforms.</h2>
              <p className={styles.sectionSubtitle}>Optimized for maximum engagement on YouTube Shorts, TikTok, and Reels.</p>
            </div>
            
            <div className={styles.phonesShowcase}>
              <div className={`${styles.phoneMockup} ${styles.sidePhone}`}>
                <ReactPlayer url={Video1} muted loop playing controls={false} width="100%" height="100%" playsinline className={styles.reactPlayerFix} />
              </div>
              <div className={`${styles.phoneMockup} ${styles.centerPhone}`}>
                <ReactPlayer url={Video2} muted loop playing controls={false} width="100%" height="100%" playsinline className={styles.reactPlayerFix} />
              </div>
              <div className={`${styles.phoneMockup} ${styles.sidePhone}`}>
                <ReactPlayer url={Video3} muted loop playing controls={false} width="100%" height="100%" playsinline className={styles.reactPlayerFix} />
              </div>
            </div>
          </section>

          {/* ================= PRICING ================= */}
          <section className={styles.pricingSection} ref={pricingRef}>
                       
            <div>
              <BuyPremium showBackButton={false} noMargin={true} />
            </div>
          </section>

          {/* ================= FAQ ================= */}
          <section className={styles.faqSection} ref={faqRef}>
            <div className={styles.sectionHeader}>
              <h2 className={styles.sectionTitle}>Frequently asked questions</h2>
            </div>
            
            <div className={styles.faqAccordion}>
              {faqs.map((item, index) => (
                <div 
                  key={index} 
                  className={`${styles.faqItem} ${activeFAQ === index ? styles.active : ""}`}
                  onClick={() => toggleFAQ(index)}
                >
                  <div className={styles.faqQuestion}>
                    <h3>{item.question}</h3>
                    <ChevronDown className={`${styles.faqIcon} ${activeFAQ === index ? styles.rotated : ""}`} />
                  </div>
                  <div className={styles.faqAnswerWrapper} style={{ maxHeight: activeFAQ === index ? '200px' : '0' }}>
                    <div className={styles.faqAnswer}>
                      <p>{item.answer}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ================= CTA ================= */}
          <section className={styles.ctaSection}>
            <div className={styles.ctaContainer}>
              <h2>Ready to Create Your First AI Video?</h2>
              <p>Join thousands of creators generating videos every day with AI.</p>
              <Link 
                to="/Dashboard" 
                className={`${styles.btnPrimary} ${styles.large}`}
                onClick={() => trackEvent("landing_final_cta")}
              >
                Start Free
              </Link>
            </div>
          </section>
        </main>

        {/* ================= FOOTER ================= */}
        <footer className={styles.premiumFooter}>
          <div className={styles.footerContent}>
            <div className={styles.footerBrand}>
              <div className={styles.navLogo}>
                <div className={styles.logoMark}><Sparkles size={16} /></div>
                <span>Generator.video</span>
              </div>
              <p>Generate professional AI videos, Shorts, Reels and TikToks in seconds.</p>
            </div>
            
            <div className={styles.footerLinks}>
              <div className={styles.linkColumn}>
                <h4>Product</h4>
                <button onClick={() => scrollTo(featureRef)}>Features</button>
                <button onClick={() => scrollTo(pricingRef)}>Pricing</button>
                <button onClick={() => scrollTo(faqRef)}>FAQ</button>
              </div>
              <div className={styles.linkColumn}>
                <h4>Resources</h4>
                <Link to="/blog">Blog</Link>
                <Link to="/Dashboard">Generate Free</Link>
              </div>
              <div className={styles.linkColumn}>
                <h4>Legal</h4>
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/refundpolicy">Refund Policy</Link>
                <Link to="/terms&condition">Terms & Conditions</Link>
              </div>
            </div>
          </div>
          {/* AI Safety Notice */}
 <div className={styles.footerNotice}>
  Generator.video does not support the creation of deepfake, face-swapped,
  or impersonation content involving real individuals. Users are prohibited
  from using the platform to create deceptive synthetic media or infringe
  upon the rights of others.
</div>
          <div className={styles.footerBottom}>
            <p>© {new Date().getFullYear()} Generator.video. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </HelmetProvider>
  );
};

// Helper component for workflow checkmarks
const CheckCircle = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
    <polyline points="22 4 12 14.01 9 11.01"></polyline>
  </svg>
);

export default LandingPage;